import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HARDWARE_CHECK_KINDS,
  HardwareKindSchema,
  hardwareCheckRow,
  hardwarePhotoEmptyState,
  hardwarePhotoRank,
  inverterIsolatorOfferView,
  seedBtyBatteryRow,
  selectedInverterIsolatorFields,
  type HardwareCheck,
  type HardwarePhotoCrop,
  type HardwareCheckSelection,
  type HardwareKind,
  type FieldHardware,
  type StoredReport,
  type StoredPhotoManifest,
} from '@cer-autofill/shared';
import { LabelPhotoCrop } from './LabelPhotoCrop.js';
import { AutoHardwareChecks } from './AutoHardwareChecks.js';
import { HardwareCheckFields, InverterIsolatorOffers } from './HardwareCheckFields.js';
import { apiErrorReason, fetchPhotoBlobUrl, requestHardwareCheck } from '../api.js';
import {
  acceptOnlyReviewsKey,
  applyHardwareCheckSelections,
  dismissManualHardwareReview,
  hardwareCheckRequestKey,
  hardwareCheckResultCurrent,
  hardwareReadingTicks,
  isCheckableHardwarePhoto,
  pendingAcceptOnlySuggestions,
  selectedHardwareFields,
  shareInFlight,
  undoManualHardwareReview,
  type AcceptOnlyReview,
} from '../lib/auto-hardware-checks.js';

export function HardwarePhotoCheck({
  report,
  hardware,
  photos,
  disabled = false,
  dismissedIds = [],
  onDismissedIdsChange = () => undefined,
  reviewResult = null,
  onApply,
  onSeedEmptyBattery,
  onAcceptOnlyReviews,
}: {
  report: Pick<StoredReport, 'jobId' | 'submittedAt' | 'status'>;
  hardware: FieldHardware;
  photos: readonly StoredPhotoManifest[];
  disabled?: boolean;
  dismissedIds?: readonly string[];
  onDismissedIdsChange?: (ids: string[]) => void;
  /** An open one-photo result. The editor leaves this empty until Check photo. */
  reviewResult?: HardwareCheck | null;
  onApply: (hardware: FieldHardware, selections: HardwareCheckSelection[]) => void;
  /** Persist Battery 1 when a BTY job was opened with no battery row. */
  onSeedEmptyBattery?: () => void;
  /** Unticked serial, capacity and breaker-size readings currently on screen. */
  onAcceptOnlyReviews?: (reviews: readonly AcceptOnlyReview[]) => void;
}) {
  const hardwareForChecks = seedBtyBatteryRow(report.jobId, hardware);
  const targets = HardwareKindSchema.options.flatMap((kind) =>
    hardwareForChecks[kind].map((_, index) => ({ kind, index, key: `${kind}:${index}` })),
  );
  const [target, setTarget] = useState('');
  const selected = targets.find((t) => t.key === target) ?? targets[0];
  const kind: HardwareKind = selected?.kind ?? 'inverters';
  const index = selected?.index ?? 0;
  const [photoId, setPhotoId] = useState(reviewResult?.photoId ?? '');
  const [result, setResult] = useState<HardwareCheck | null>(reviewResult);
  const [dismissedResult, setDismissedResult] = useState<HardwareCheck | null>(null);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [isolatorOverrides, setIsolatorOverrides] = useState<Record<string, boolean>>({});
  const [isolatorTarget, setIsolatorTarget] = useState<number>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState<HardwarePhotoCrop>();
  const [selectingCrop, setSelectingCrop] = useState(false);
  const generation = useRef(0);
  const seenPhotoIds = useRef(new Set<string>());
  const ranFor = useRef('');
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );
  const eligible = photos
    .filter((p) => isCheckableHardwarePhoto(p, kind, index, hardwareForChecks))
    .sort(
      (a, b) =>
        hardwarePhotoRank(b.promptKey, kind, index, hardwareForChecks) -
        hardwarePhotoRank(a.promptKey, kind, index, hardwareForChecks),
    );
  const photo = eligible.find((p) => p.photoId === photoId);
  useEffect(() => {
    let cancelled = false;
    let url: string | undefined;
    setImage(null);
    setImageLoaded(false);
    setZoom(1);
    setCrop(undefined);
    setSelectingCrop(false);
    if (photo)
      void fetchPhotoBlobUrl(report.jobId, photo.photoId)
        .then((value) => {
          url = value;
          if (!cancelled) setImage(value);
          else if (value.startsWith('blob:')) URL.revokeObjectURL(value);
        })
        .catch(() => {
          if (!cancelled) setError('Could not load the photo. Select it again to retry.');
        });
    return () => {
      cancelled = true;
      if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, [report.jobId, photo?.photoId, photo?.uploadedAt]);
  const row = result ? hardwareCheckRow(hardwareForChecks, result.kind, result.index) : undefined;
  // Compare the checked row only. A reading applied to another unit must not
  // mark this comparison out of date.
  const valid =
    !!result &&
    result.kind === kind &&
    result.index === index &&
    hardwareCheckResultCurrent(result, {
      hardware: hardwareForChecks,
      submittedAt: report.submittedAt,
      photo: photo ? { photoId: photo.photoId, uploadedAt: photo.uploadedAt } : undefined,
      crop,
    });
  const ticks = valid && result ? hardwareReadingTicks(result, row, []) : [];
  const selectedFields = selectedHardwareFields(ticks, overrides);
  function reset() {
    setResult(null);
    setDismissedResult(null);
    setOverrides({});
    setIsolatorOverrides({});
    setIsolatorTarget(undefined);
    setError(null);
    setNotice(null);
  }
  const offerView =
    valid && result
      ? inverterIsolatorOfferView({
          hardware: hardwareForChecks,
          check: result,
          target: isolatorTarget,
        })
      : undefined;
  const isolatorFields = offerView
    ? selectedInverterIsolatorFields(offerView.offers, isolatorOverrides)
    : [];
  const isolatorApplication =
    offerView?.index !== undefined && isolatorFields.length
      ? { index: offerView.index, fields: isolatorFields }
      : undefined;
  async function check() {
    if (!photo || !selected) return;
    const requestGeneration = ++generation.current;
    setBusy(true);
    reset();
    const request = {
      kind,
      index,
      photoId: photo.photoId,
      hardware: hardwareForChecks,
      ...(crop ? { crop } : {}),
    };
    try {
      const next = await shareInFlight(
        hardwareCheckRequestKey({
          jobId: report.jobId,
          submittedAt: report.submittedAt,
          request,
        }),
        () => requestHardwareCheck(report.jobId, report.submittedAt, request),
      );
      if (generation.current === requestGeneration) {
        if (next.kind === 'batteries') onSeedEmptyBattery?.();
        setResult(next);
      }
    } catch (e) {
      if (generation.current === requestGeneration) setError(apiErrorReason(e));
    } finally {
      if (generation.current === requestGeneration) setBusy(false);
    }
  }
  const checkRef = useRef(check);
  checkRef.current = check;
  const eligibleKey = eligible.map((item) => item.photoId).join(',');
  useEffect(() => {
    const fresh = eligible.filter((item) => !seenPhotoIds.current.has(item.photoId));
    for (const item of photos) seenPhotoIds.current.add(item.photoId);
    if (fresh.length === 1) {
      setPhotoId(fresh[0]!.photoId);
      return;
    }
    if (!photoId || !eligible.some((item) => item.photoId === photoId))
      setPhotoId(eligible[0]?.photoId ?? '');
  }, [eligibleKey, kind, index]);
  useEffect(() => {
    if (!photoId || !selected) return;
    const token = `${kind}:${index}:${photoId}`;
    if (ranFor.current === token) return;
    ranFor.current = token;
    void checkRef.current();
  }, [photoId, kind, index, selected]);
  useEffect(() => {
    if (hardware.batteries.length > 0 || !report.jobId.startsWith('BTY')) return;
    onSeedEmptyBattery?.();
  }, [hardware.batteries.length, report.jobId, onSeedEmptyBattery]);
  const [autoReviews, setAutoReviews] = useState<AcceptOnlyReview[]>([]);
  const rememberAutoReviews = useCallback((reviews: readonly AcceptOnlyReview[]) => {
    setAutoReviews((current) =>
      acceptOnlyReviewsKey(current) === acceptOnlyReviewsKey(reviews) ? current : [...reviews],
    );
  }, []);
  const manualReviews =
    valid && result
      ? pendingAcceptOnlySuggestions([result], hardwareForChecks, { [result.id]: overrides })
      : [];
  const acceptOnlyReviews = [...autoReviews, ...manualReviews].filter(
    (item, index, all) =>
      all.findIndex((other) => other.resultId === item.resultId && other.field === item.field) ===
      index,
  );
  const acceptOnlyKey = acceptOnlyReviewsKey(acceptOnlyReviews);
  useEffect(() => {
    onAcceptOnlyReviews?.(acceptOnlyReviews);
  }, [acceptOnlyKey, onAcceptOnlyReviews]);
  return (
    <section className="label-suggestions hardware-photo-check" aria-label="Hardware photo check">
      <h4>Check against photos</h4>
      <p className="muted">
        Photos for a single unit are checked when Edit opens. Compare the readings, then apply
        selected changes to your draft.
      </p>
      <AutoHardwareChecks
        report={report}
        hardware={hardwareForChecks}
        photos={photos}
        disabled={disabled}
        dismissedIds={dismissedIds}
        onDismissedIdsChange={onDismissedIdsChange}
        onApply={onApply}
        onAcceptOnlyReviews={rememberAutoReviews}
        {...(onSeedEmptyBattery ? { onSeedEmptyBattery } : {})}
      />
      <p>
        <strong>Check a photo yourself</strong>
      </p>
      <div className="form-grid">
        <label>
          Hardware row
          <select
            value={selected?.key ?? ''}
            disabled={busy || disabled}
            onChange={(e) => {
              setTarget(e.target.value);
              setPhotoId('');
              reset();
            }}
          >
            {!targets.length && <option value="">Add hardware below first</option>}
            {targets.map((t) => (
              <option key={t.key} value={t.key}>
                {HARDWARE_CHECK_KINDS[t.kind].label} {t.index + 1}
              </option>
            ))}
          </select>
        </label>
        <label>
          Linked photo
          <select
            value={photoId}
            disabled={busy || disabled}
            onChange={(e) => {
              setPhotoId(e.target.value);
              reset();
            }}
          >
            <option value="">Choose a photo for this unit…</option>
            {eligible.map((p) => (
              <option key={p.photoId} value={p.photoId}>
                {p.filename}
              </option>
            ))}
          </select>
        </label>
      </div>
      {!eligible.length && <p>{hardwarePhotoEmptyState(kind)}</p>}
      {eligible.length > 0 && !photoId && (
        <p className="muted">Choose a photo for this unit. You can change it after a reading.</p>
      )}
      <div className="hardware-photo-check__actions">
        <button
          type="button"
          disabled={busy || disabled || !photo || !selected}
          onClick={() => void check()}
        >
          {busy ? 'Reading photo…' : 'Check photo'}
        </button>
        <span className="muted">
          {crop
            ? 'The selected photo and label crop are compared by AI.'
            : 'The selected photo is read twice to check each suggestion.'}
        </span>
      </div>
      <details className="report-help">
        <summary>What can be checked?</summary>
        <p>
          Make, model, serial numbers and readable label ratings. Check the photo belongs to the
          selected unit. Quantities, installation locations, system totals and inspection judgements
          remain manual. Unreadable details are left unchanged. An Integrated DC isolator, or one
          whose location is still blank, can take its voltage and current from the inverter
          specification label. One inverter and one isolator are paired automatically. Several units
          are left for you to choose. A separate isolator label is used instead when it has a
          reading.
        </p>
        <p>
          Review differences before applying. Matching readings need no change. Serial numbers
          identify individual units; a panel serial reading updates the example serial and keeps
          other recorded serials.
        </p>
      </details>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {result && !valid && (
        <p role="status">
          Hardware or photo changed. Check the photo again for an up-to-date comparison.
        </p>
      )}
      {!result && dismissedResult && (
        <div className="hardware-photo-check__actions">
          <button
            type="button"
            disabled={disabled || busy}
            onClick={() => {
              const restored = undoManualHardwareReview(dismissedResult);
              setResult(restored.open);
              setDismissedResult(restored.undo);
            }}
          >
            Undo dismiss
          </button>
        </div>
      )}
      <div className="label-suggestions__review">
        {image && (
          <div>
            <label>
              Photo zoom
              <input
                aria-label="Hardware photo zoom"
                type="range"
                min="1"
                max="4"
                step="0.25"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </label>
            {photo?.mimeType !== 'image/webp' && (
              <div className="hardware-photo-check__actions">
                <button
                  type="button"
                  disabled={busy || disabled || !imageLoaded}
                  aria-pressed={selectingCrop}
                  onClick={() => setSelectingCrop(!selectingCrop)}
                >
                  Crop label
                </button>
                {crop && (
                  <button
                    type="button"
                    disabled={busy || disabled}
                    onClick={() => {
                      setCrop(undefined);
                      reset();
                    }}
                  >
                    Use full photo
                  </button>
                )}
              </div>
            )}
            <LabelPhotoCrop
              image={image}
              zoom={zoom}
              crop={crop}
              selecting={selectingCrop && !busy && !disabled}
              onCrop={(next) => {
                setCrop(next);
                reset();
              }}
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageLoaded(false);
                setError('Could not display the photo. Reload it before applying changes.');
              }}
            />
          </div>
        )}
        {valid && result && (
          <div aria-live="polite">
            {result.needsClearerPhoto && (
              <p>A clearer label photo may help with unreadable details.</p>
            )}
            <HardwareCheckFields
              result={result}
              row={row}
              overrides={overrides}
              disabled={disabled || busy}
              photoPromptKey={photo?.promptKey}
              onToggle={(field, checked) =>
                setOverrides((current) => ({ ...current, [field]: checked }))
              }
            />
            {offerView && (
              <InverterIsolatorOffers
                view={offerView}
                overrides={isolatorOverrides}
                disabled={disabled || busy}
                onIsolatorIndex={setIsolatorTarget}
                onToggle={(field, checked) =>
                  setIsolatorOverrides((current) => ({ ...current, [field]: checked }))
                }
              />
            )}
            <button
              type="button"
              disabled={
                disabled ||
                busy ||
                !imageLoaded ||
                (!selectedFields.length && !isolatorFields.length)
              }
              onClick={() => {
                if (!valid || !imageLoaded || !result) return;
                try {
                  const applied = applyHardwareCheckSelections(hardwareForChecks, [
                    {
                      result,
                      fields: selectedFields,
                      ...(isolatorApplication ? { dcIsolator: isolatorApplication } : {}),
                    },
                  ]);
                  onApply(applied.hardware, applied.selections);
                  setResult(null);
                  setDismissedResult(null);
                  setOverrides({});
                  setNotice('Selected readings applied to draft.');
                } catch (applyFailure) {
                  setError(
                    applyFailure instanceof Error &&
                      (applyFailure.message === 'Hardware row no longer exists' ||
                        applyFailure.message === 'Invalid hardware reading' ||
                        applyFailure.message === 'DC isolator does not take inverter ratings')
                      ? applyFailure.message
                      : 'Could not apply those readings.',
                  );
                }
              }}
            >
              Apply selected to draft
              {selectedFields.length + isolatorFields.length
                ? ` (${selectedFields.length + isolatorFields.length})`
                : ''}
            </button>
          </div>
        )}
        {result && (
          <div className="hardware-photo-check__actions">
            <button
              type="button"
              disabled={disabled || busy}
              onClick={() => {
                const dismissed = dismissManualHardwareReview(result);
                setDismissedResult(dismissed.undo);
                setResult(dismissed.open);
                setOverrides({});
                setNotice(null);
                setError(null);
              }}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
